import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  getAccountExternalLinks,
  getAccountExternalLinkById,
  createAccountExternalLink,
  updateAccountExternalLink,
  deleteAccountExternalLink,
  getAccountExternalLinkTypes,
} from '~/api/accountExternalLinks';
import { AccountExternalLink } from '~/types/AccountExternalLink';
import { extractEncodedKey } from '~/dataProvider';

// Query key helpers
const accountExternalLinksKey = (accountId: string) => ['accountExternalLinks', accountId];
const accountExternalLinkKey = (id: string) => ['accountExternalLink', id];
const accountExternalLinkTypesKey = ['accountExternalLinks', 'linkTypes'];

export const useClearAccountExternalLinksCache = (id: string) => {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries(accountExternalLinksKey(id));
  };
};

export const useAccountExternalLinksQuery = (
  params: {
    accountId: string;
    page: number;
    perPage: number;
    ids?: string;
  },
  options?: {
    onSuccess?: (res: AccountExternalLink[]) => void;
    enabled?: boolean;
  }
) => {
  const { accountId } = params;
  return useQuery<AccountExternalLink[]>(
    accountExternalLinksKey(accountId),
    () =>
      getAccountExternalLinks(params).then((res) =>
        // Handle encodedKey extraction for user UI
        res.data?.map((link) => extractEncodedKey(link))
      ),
    options
  );
};

export const useAccountExternalLinkQuery = (
  id: string,
  options?: {
    onSuccess?: (res: AccountExternalLink) => void;
    enabled?: boolean;
  }
) => {
  return useQuery<AccountExternalLink>(
    accountExternalLinkKey(id),
    () => getAccountExternalLinkById(id).then((res) => res.data),
    options
  );
};

export const useAccountExternalLinkCreate = () => {
  const queryClient = useQueryClient();
  return useMutation((data: Partial<AccountExternalLink>) => createAccountExternalLink(data), {
    onSuccess() {
      queryClient.invalidateQueries('accountExternalLinks');
    },
  });
};

export const useAccountExternalLinkUpdate = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, data }: { id: string; data: Partial<AccountExternalLink> }) =>
      updateAccountExternalLink(id, data),
    {
      onSuccess() {
        queryClient.invalidateQueries('accountExternalLinks');
      },
    }
  );
};

export const useAccountExternalLinkDelete = (accountId: string) => {
  const queryClient = useQueryClient();
  return useMutation((id: string) => deleteAccountExternalLink(id), {
    onSuccess() {
      queryClient.invalidateQueries(accountExternalLinksKey(accountId));
    },
  });
};

interface Options {
  enabled?: boolean;
}

export const useAccountExternalLinkTypes = (options?: Options) => {
  return useQuery(accountExternalLinkTypesKey, getAccountExternalLinkTypes, { ...options });
};
